import React from 'react';
import { 
    Box, 
    Container, 
    Typography, 
    Grid, 
    Card, 
    CardMedia, 
    CardContent, 
    CardActions, 
    Button, 
    Chip 
} from '@mui/material';

import Img1 from '../assets/images/tripan.jpg';
import Img2 from '../assets/images/nitr.jpg';
import Img3 from '../assets/images/lep2.jpeg';
import Img4 from '../assets/images/nu1.jpeg';
import Img5 from '../assets/images/honey.avif';
import Img6 from '../assets/images/estu.webp';

// --- Color Palette (as requested) ---
// Define colors for easy management and consistency.
const PALETTE = {
  stoneWhite: '#f1f1f1',
  peacockTeal: '#005B5C',
  saffronOrange: '#FCA510',
  textPrimary: '#1A2027',
  textSecondary: '#5F5F5F',
};

// --- Journey Data ---
// Separating data from presentation makes the component cleaner and easier to update.
const journeys = [
  {
    image: Img1,
    tag: '8 DAYS / 7 NIGHTS',
    title: 'The Ancient Kingdoms Trail',
    description: 'Journey through the heart of the Cultural Triangle. Ascend the legendary Sigiriya rock, explore the ruins of Polonnaruwa, and feel the spiritual pulse of Kandy.',
  },
  {
    image: Img2,
    tag: '12 DAYS / 11 NIGHTS',
    title: 'Tea Hills to Golden Sands',
    description: 'A journey of beautiful contrasts. Ride the iconic train through emerald tea plantations, then unwind on the sun-drenched beaches of the pristine south coast.',
  },
  {
    image: Img3,
    tag: '7 DAYS / 6 NIGHTS',
    title: 'The Leopard\'s Path Safari',
    description: 'An unforgettable adventure for the wildlife enthusiast. Track the elusive leopard in Yala and witness the great elephant gathering in Udawalawe.',
  },
  {
    image: Img4,
    tag: '9 DAYS / 8 NIGHTS',
    title: 'Highlands & Rapids Quest',
    description: 'For the thrill-seeker. Conquer the cloud forests of Horton Plains, trek the rugged Knuckles mountain range, and brave the white-water rapids of Kitulgala.',
  },
  {
    image: Img5,
    tag: '14 DAYS / 13 NIGHTS',
    title: 'The Coastal Serenade',
    description: 'A journey of pure romance and barefoot luxury. Unwind in secluded boutique villas, enjoy private candlelit dinners on the beach, and wander the historic streets of Galle Fort.',
  },
  {
    image: Img6,
    tag: '5 DAYS / 4 NIGHTS | ANNUAL EVENT',
    title: 'The Sacred Festival of Light',
    description: 'Immerse yourself in one of Asia\'s most dazzling cultural spectacles. Witness the vibrant procession of dancers, drummers, and majestic elephants under the stars, from a vantage point that takes you to the spiritual heart of this ancient tradition.',
  },
];

// --- The Main Component ---
const SignatureJourneys = () => {
  return (
    <Box
      component="section"
      sx={{
        py: { xs: 6, md: 10 }, // Generous vertical padding for breathing room
        backgroundColor: PALETTE.stoneWhite,
      }}
    >
      <Container maxWidth="lg">
        {/* === SECTION HEADING === */}
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography
                        variant="h3"
                        component="h2"
                        sx={{
                          fontWeight: 500,
                          mb: 3,
                          lineHeight: 1.3,
                          fontFamily: 'serif',
                          color:'#005F69'
                        }}
                      >
            Explore Our Travel Themes
          </Typography>
          <Typography
            variant="h6"
            component="p"
            sx={{
              color: PALETTE.textSecondary,
              maxWidth: '650px',
              mx: 'auto', // Center the text block
              fontWeight: 400,
            }}
          >
            Inspiration for your adventure. Each journey is a starting point, ready to be tailored to your personal travel dreams.
          </Typography>
        </Box>

        {/* === TRIP PLAN CARDS GRID === */}
        <Grid container spacing={4}>
          {journeys.map((journey) => (
            <Grid item xs={12} sm={6} md={4} key={journey.title}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: '12px', // Softer edges
                  boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
                  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 32px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <CardMedia
                    component="img"
                    height="220"
                    image={journey.image}
                    alt={journey.title}
                  />
                  {/* Subtle Tag using MUI Chip for a clean look */}
                  <Chip 
                    label={journey.tag}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 16,
                      left: 16,
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                      color: 'white',
                      fontWeight: 600,
                      letterSpacing: '0.5px',
                      backdropFilter: 'blur(4px)',
                      borderRadius: '6px'
                    }}
                  />
                </Box>
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Typography
                    gutterBottom
                    variant="h5"
                    component="h3"
                    // FIXED: Removed the duplicate 'color' property here
                    sx={{ fontWeight: 600, mb: 1, color:'#005F69' }}
                  >
                    {journey.title}
                  </Typography>
                  <Typography variant="body2" color={PALETTE.textSecondary}>
                    {journey.description}
                  </Typography>
                </CardContent>
                <CardActions sx={{ px: 3, pb: 3, mt: 'auto', justifyContent: 'space-between' }}>
                    <Button 
                        variant="contained" 
                        size="large"
                        sx={{
                            backgroundColor: PALETTE.peacockTeal,
                            fontWeight: 600,
                            px: 3,
                            py: 1.5,
                            '&:hover': { backgroundColor: '#004243' }
                        }}
                    >
                        View Itinerary
                    </Button>
                    <Button 
                        variant="text" 
                        sx={{ 
                            color: PALETTE.peacockTeal, 
                            fontWeight: 600,
                            '&:hover': { backgroundColor: 'rgba(0, 91, 92, 0.08)' } 
                        }}
                    >
                        Customize This Trip
                    </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
        
        {/* === CUSTOM TRIP CTA === */}
        <Box sx={{ textAlign: 'center', mt: { xs: 8, md: 12 } }}>
          <Typography
              variant="h3"
              component="h2"
              sx={{
                fontWeight: 500,
                mb: 3,
                lineHeight: 1.3,
                fontFamily: 'serif',
                color:'#005F69'
              }}
            >
            Craft a Journey That is Uniquely Yours
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: PALETTE.textSecondary,
              maxWidth: '600px',
              mx: 'auto',
              mb: 4,
            }}
          >
            Do you dream of combining a cultural deep-dive with a relaxing beach escape? Or a wildlife safari with a wellness retreat? The themes above are just the beginning. Our true passion is designing a journey that blends your unique interests into a seamless, unforgettable story.</Typography>
          <Button
            variant="contained"
            size="large"
            sx={{
              backgroundColor: PALETTE.saffronOrange,
              color: PALETTE.textPrimary,
              fontWeight: 700,
              fontSize: '1rem',
              py: 2,
              px: 6,
              borderRadius: '8px',
              transition: 'transform 0.2s ease',
              '&:hover': {
                backgroundColor: '#e69500', // A slightly darker shade on hover
                transform: 'scale(1.03)',
              },
            }}
          >
            START DESIGNING YOUR TRIP
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default SignatureJourneys;